import Vue from 'vue';
import BootstrapVue from 'bootstrap-vue';
import Vuelidate from 'vuelidate';
import axios from 'axios';
import VueAxios from 'vue-axios';
import Rollbar from 'vue-rollbar';

// Third party UI components
import VueProgressBar from 'vue-progressbar';
import VueAWN from 'vue-awesome-notifications';
import VModal from 'vue-js-modal';

// Icons aplenty
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

// In use icons
import {
  faClock,
  faUser,
  faMinusCircle,
  faPen,
  faUserPlus,
  faCalendarAlt,
  faCheckCircle,
  faEnvelope,
  faFileInvoiceDollar,
  faFolder,
  faFolderOpen,
  faBan,
  faClone,
} from '@fortawesome/free-solid-svg-icons';

// Application ervices
import App from './App.vue';
import router from './router';
import store from './store/store';
import AuthService from '@/services/auth.service';

// Shared UI components
import ContentPanel from '@/components/commonui/ContentPanel.vue';

// Mapping stuff
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Main assets
import '@/assets/app.scss';

// Import the various layouts
import Default from '@/layouts/Default.vue';
import PrintLandscape from '@/layouts/PrintLandscape.vue';
import PrintPortrait from '@/layouts/PrintPortrait.vue';
import Blank from '@/layouts/Blank.vue';

Vue.component('default-layout', Default);
Vue.component('print-landscape-layout', PrintLandscape);
Vue.component('print-portrait-layout', PrintPortrait);
Vue.component('blank-layout', Blank);

Vue.config.productionTip = false;

// Setup bootstrap-vue
Vue.use(BootstrapVue);

// Setup the progress bar
Vue.use(VueProgressBar, { color: 'darkorange', failedColor: 'red', height: '2px' });

// Setup the toast notifications
const toastOptions = { position: 'top-right', durations: { global: 2000, warning: 4000, alert: 0 } };
Vue.use(VueAWN, toastOptions);

// Setup the modal dialog handler, and set the
// name of the component to 'vue-modal'
Vue.use(VModal, { componentName: 'vue-modal' });

// Wire in axios
Vue.use(VueAxios, axios);

// Add in the validator
Vue.use(Vuelidate);

// register all the icons in the library
library.add(
  faClock,
  faUser,
  faMinusCircle,
  faPen,
  faUserPlus,
  faCalendarAlt,
  faCheckCircle,
  faEnvelope,
  faFileInvoiceDollar,
  faFolder,
  faFolderOpen,
  faBan,
  faClone
);

Vue.component('font-awesome-icon', FontAwesomeIcon);

// Register some globally used ui components
Vue.component('content-panel', ContentPanel);

// Setup the leaflet mapping components
// @ts-ignore
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Rollbar implementation
Vue.use(Rollbar, {
  accessToken: process.env.ROLLBAR_ACCESSTOKEN,
  captureUncaught: true,
  captureUnhandledRejections: true,
  enabled: true,
  environment: process.env.ROLLBAR_ENVIRONMENT,
  payload: {
    client: {
      javascript: {
        code_version: '1.0',
        source_map_enabled: true,
        guess_uncaught_frames: true,
      },
    },
  },
});

new Vue({
  router,
  store,
  render: h => h(App),
}).$mount('#app');

const auth = new AuthService();

// axios.defaults.withCredentials = true;

axios.interceptors.request.use(
  async (config: any) => {
    // const authToken = store.getters['auth/authToken'];
    const authToken = await auth.getAccessToken();
    if (authToken) {
      // console.log('injecting bearer token:' + config.path);
      config.headers.Authorization = `Bearer ${authToken}`;
    } else {
      // console.log('no bearer token to inject:' + config.path);
    }
    // this tells axios that ANY STATUS is a good status
    // otherwise it'll reject the http call with its own funy Error object
    // and the openapi2tsclient Axios template doesn't do try catching of the calls
    // so you never get any details out at the caller for, say, a 422 error
    // We can't be any more selective about "this is good this is bad" until the axios template gets an overhaul
    config.validateStatus = (status: number) => {
      return true;
    };
    return config;
  },
  (err: any) => {
    return Promise.reject(err);
  }
);

axios.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (error.response) {
      if (error.response.status === 401) {
        // route to login, and set the return url to the current url
        console.log('401 Unauthorised received.');
        auth.login();
      }
    }
    return error;
  }
);
